import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import {
  nhStats,
  nhDemographics,
  nhIncomeTrend,
  nhPovertyTrend,
  nhCrimeTrend,
  nhIndustrialGrowth,
  nhEducationScores,
  nhSectorJobs,
  nhResidentPriorities,
} from "@/lib/northHoustonData";

const navy = "#0A192F";
const slate = "#475569";
const red = "#8B0000";
const grid = "#CBD5E1";

function SectionLabel({ children }) {
  return (
    <div className="flex items-center gap-3 mb-3">
      <span className="h-px w-8 bg-[#8B0000]" />
      <span className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#8B0000]">
        {children}
      </span>
    </div>
  );
}

function ChartCard({ title, subtitle, children, testId, large = false, badge = "Verified" }) {
  const badgeColor = badge === "Verified" ? "text-[#0A192F]" : "text-[#8B0000]";
  return (
    <div
      data-testid={testId}
      className={`bg-white border border-[#CBD5E1] p-6 lg:p-7 ${large ? "lg:col-span-2" : ""}`}
    >
      <div className="flex items-start justify-between gap-4 mb-1">
        <h3 className="font-display text-lg text-[#0A192F]">{title}</h3>
        <span className={`font-mono text-[9px] uppercase tracking-widest ${badgeColor} mt-1.5 shrink-0`}>
          {badge}
        </span>
      </div>
      <p className="text-xs text-[#475569] mb-5">{subtitle}</p>
      <div className="h-56 lg:h-64">{children}</div>
    </div>
  );
}

export default function LocalContextNH() {
  const fmt = (n) => `$${(n / 1000).toFixed(1)}k`;
  return (
    <section
      id="context"
      data-testid="section-local-context-nh"
      className="bg-[#F9F7F1] py-24 lg:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="grid lg:grid-cols-12 gap-10 mb-14">
          <div className="lg:col-span-5">
            <SectionLabel>01 — The Local Context</SectionLabel>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              From Greenspoint to corridor. <em className="italic">The transition is real — and incomplete.</em>
            </h2>
          </div>
          <div className="lg:col-span-7 lg:pt-2">
            <p className="text-[#334155] text-base leading-relaxed">
              The North Houston District has executed one of Texas's most consequential
              neighborhood turnarounds — from the Greenspoint of the early 2000s into
              a {nhStats.industrial_to_office_ratio} industrial-to-office corridor with{" "}
              <strong className="text-[#0A192F]">{nhStats.industrial_sqft_million}M+ sq ft</strong>{" "}
              of warehouse and distribution space anchored {nhStats.iah_distance_miles} miles
              from IAH. The infrastructure has been rebuilt. The employers are
              arriving. What the moment now demands is the trained human chain that
              connects the{" "}
              <strong className="text-[#0A192F]">{nhStats.population.toLocaleString()}</strong>{" "}
              residents who live in the district to the jobs the district is producing.
            </p>
            <p className="mt-4 text-xs text-[#64748B] font-mono uppercase tracking-wider">
              Snapshot figures derived from U.S. Census ACS 5-Year (Harris County tract
              series — district boundaries) and North Houston District corporate
              filings (2025). Multi-year trend lines illustrative from ACS / FBI UCR.
            </p>
          </div>
        </div>

        {/* Quick stat ribbon */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#CBD5E1] border border-[#CBD5E1] mb-12">
          {[
            { k: `${nhStats.poverty_rate_pct}%`, v: "Overall Poverty", accent: red },
            { k: `${nhStats.hispanic_latino_pct}%`, v: "Hispanic / Latino", accent: navy },
            { k: `${nhStats.black_alone_pct}%`, v: "Black / African American", accent: red },
            { k: `${nhStats.industrial_sqft_million}M+`, v: "Sq Ft Industrial (District)", accent: navy },
          ].map((s) => (
            <div
              key={s.v}
              data-testid={`context-nh-ribbon-${s.v.toLowerCase().replace(/\s/g, "-")}`}
              className="bg-white px-6 py-7"
            >
              <div className="font-display text-3xl" style={{ color: s.accent }}>
                {s.k}
              </div>
              <div className="font-mono text-[10px] uppercase tracking-widest text-[#475569] mt-2">
                {s.v}
              </div>
            </div>
          ))}
        </div>

        {/* Charts grid */}
        <div className="grid lg:grid-cols-2 gap-6">
          <ChartCard
            testId="chart-nh-income-trend"
            title="Median Household Income — North Houston District"
            subtitle="$41,280 (ACS 5-Yr approx). Rising — but still ~30% below Harris County metro average."
            large
            badge="Trend · Illustrative"
          >
            <ResponsiveContainer>
              <AreaChart data={nhIncomeTrend} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="gNHIncome" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={navy} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={navy} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke={grid} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="year" stroke={slate} tickLine={false} axisLine={{ stroke: grid }} />
                <YAxis stroke={slate} tickFormatter={fmt} tickLine={false} axisLine={{ stroke: grid }} />
                <Tooltip
                  formatter={(v) => [`$${v.toLocaleString()}`, "Median Income"]}
                  cursor={{ stroke: red, strokeWidth: 1 }}
                />
                <Area type="monotone" dataKey="median_income" stroke={navy} strokeWidth={2} fill="url(#gNHIncome)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            testId="chart-nh-poverty-trend"
            title="Poverty Rates — Overall vs. Children"
            subtitle="Declining since 2015 alongside corridor transformation — still elevated relative to Houston metro."
            badge="Mixed"
          >
            <ResponsiveContainer>
              <LineChart data={nhPovertyTrend} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={grid} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="year" stroke={slate} tickLine={false} axisLine={{ stroke: grid }} />
                <YAxis stroke={slate} tickFormatter={(v) => `${v}%`} tickLine={false} axisLine={{ stroke: grid }} domain={[20, 45]} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="poverty" name="Overall" stroke={navy} strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="child_poverty" name="Children under 18" stroke={red} strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            testId="chart-nh-demographics"
            title="Demographic Composition"
            subtitle="Hispanic-Latino and Black/African American majorities. Source: ACS 5-Yr."
            badge="Verified"
          >
            <ResponsiveContainer>
              <PieChart>
                <Pie data={nhDemographics} dataKey="pct" nameKey="group" innerRadius="48%" outerRadius="78%" paddingAngle={2} stroke="#F9F7F1" strokeWidth={2}>
                  {nhDemographics.map((d, i) => (
                    <Cell key={i} fill={d.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => `${v}%`} />
                <Legend layout="vertical" verticalAlign="middle" align="right" wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            testId="chart-nh-crime-trend"
            title="Crime per 1,000 Residents"
            subtitle="Documented decline through the district's transformation — Bridge stabilization sustains the trend."
            badge="Trend · Illustrative"
          >
            <ResponsiveContainer>
              <LineChart data={nhCrimeTrend} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={grid} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="year" stroke={slate} tickLine={false} axisLine={{ stroke: grid }} />
                <YAxis stroke={slate} tickLine={false} axisLine={{ stroke: grid }} tickFormatter={(v) => `${v}`} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Line type="monotone" dataKey="property" name="Property" stroke={navy} strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="violent" name="Violent" stroke={red} strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            testId="chart-nh-industrial-growth"
            title="Industrial Absorption — District Square Footage (millions)"
            subtitle="From 13M sq ft in 2019 to 20M+ in 2024 · 2:1 industrial-to-office. Source: North Houston District corporate, 2025."
            large
            badge="Verified"
          >
            <ResponsiveContainer>
              <BarChart data={nhIndustrialGrowth} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={grid} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="year" stroke={slate} tickLine={false} axisLine={{ stroke: grid }} />
                <YAxis stroke={slate} tickFormatter={(v) => `${v}M`} tickLine={false} axisLine={{ stroke: grid }} domain={[0, 25]} />
                <Tooltip formatter={(v) => `${v}M sq ft`} />
                <Bar dataKey="sqft" name="Industrial Sq Ft" fill={red} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            testId="chart-nh-education-scores"
            title="Aldine ISD vs. Texas State (STAAR · Approaches Grade Level)"
            subtitle="District-serving Aldine ISD trails state averages on reading, math, and science — the achievement gap the Bridge family-track closes. Source: TEA TAPR (illustrative)."
            badge="TEA TAPR"
          >
            <ResponsiveContainer>
              <BarChart data={nhEducationScores} margin={{ top: 8, right: 24, left: 0, bottom: 0 }}>
                <CartesianGrid stroke={grid} strokeDasharray="2 4" vertical={false} />
                <XAxis dataKey="subject" stroke={slate} tickLine={false} axisLine={{ stroke: grid }} />
                <YAxis stroke={slate} tickFormatter={(v) => `${v}%`} tickLine={false} axisLine={{ stroke: grid }} domain={[0, 100]} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="aldine" name="Aldine ISD" fill={red} radius={[2, 2, 0, 0]} />
                <Bar dataKey="texas" name="Texas State" fill={navy} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            testId="chart-nh-sector-jobs"
            title="Corridor Jobs by Sector — Where Bridge Routes Residents"
            subtitle="Approximate active jobs in the district + corridor. Bridge connectors route households into every category — logistics-first, but diversified by design."
            badge="District Corporate"
          >
            <ResponsiveContainer>
              <BarChart
                data={nhSectorJobs}
                layout="vertical"
                margin={{ top: 8, right: 24, left: 0, bottom: 0 }}
              >
                <CartesianGrid stroke={grid} strokeDasharray="2 4" horizontal={false} />
                <XAxis type="number" stroke={slate} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} tickLine={false} axisLine={{ stroke: grid }} />
                <YAxis type="category" dataKey="sector" stroke={slate} tickLine={false} axisLine={{ stroke: grid }} width={170} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => `${v.toLocaleString()} jobs`} />
                <Bar dataKey="jobs" name="Active jobs" fill={navy} radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <ChartCard
            testId="chart-nh-resident-priorities"
            title="Resident Priorities — What the District is Asking For"
            subtitle="Stated priorities from district resident engagement. Living-wage jobs is the #1 resident ask — and the centerpiece of the Bridge corridor pipeline."
            large
            badge="Engagement · Illustrative"
          >
            <ResponsiveContainer>
              <BarChart data={nhResidentPriorities} margin={{ top: 8, right: 24, left: 0, bottom: 0 }} layout="vertical">
                <CartesianGrid stroke={grid} strokeDasharray="2 4" horizontal={false} />
                <XAxis type="number" stroke={slate} tickFormatter={(v) => `${v}%`} tickLine={false} axisLine={{ stroke: grid }} domain={[0, 100]} />
                <YAxis type="category" dataKey="label" stroke={slate} tickLine={false} axisLine={{ stroke: grid }} width={230} tick={{ fontSize: 10 }} />
                <Tooltip formatter={(v) => `${v}%`} />
                <Bar dataKey="pct" name="% of residents" fill={red} radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    </section>
  );
}
